import { useTranslation } from 'react-i18next';
import { Github, Globe } from 'lucide-react';
import { Project } from '../types';

export const GooglePlayIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M1.571 23.664l10.531-10.501 3.455 3.396-12.058 6.67c-.276.153-.587.228-.898.228-.372 0-.74-.107-1.03-.793zM22 12c0 .607-.314 1.148-.828 1.449l-3.967 2.193-3.816-3.752 3.816-3.752 3.967 2.193C21.686 10.852 22 11.393 22 12zM1.57.336C1.86-.357 2.228-.464 2.6-.464c.31 0 .622.076.898.228l12.058 6.67-3.455 3.396L1.57.336zM.088 1.873c-.056.18-.088.37-.088.565v19.123c0 .196.032.386.088.566L11.08 12 .088 1.873z"/>
  </svg>
);

export const AppleIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701z"/>
  </svg>
);

/** Website / store / GitHub buttons. `compact` = icon-only round buttons (project rows). */
export default function ProjectLinks({ project: p, compact, className }: { project: Project; compact?: boolean; className: string }) {
  const { t } = useTranslation();
  const links = [
    { url: p.website_url, label: t('website'), icon: <Globe size={15} /> },
    { url: p.app_store_url, label: 'App Store', icon: <AppleIcon size={15} /> },
    { url: p.play_store_url, label: 'Google Play', icon: <GooglePlayIcon size={14} /> },
    { url: p.github_url, label: 'GitHub', icon: <Github size={15} /> },
  ].filter(l => l.url);
  if (links.length === 0) return null;

  return (
    <div className="relative z-10 flex flex-wrap gap-2">
      {links.map(l => (
        <a
          key={l.label}
          href={l.url!}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={compact ? l.label : undefined}
          title={compact ? l.label : undefined}
          className={`${compact ? 'w-11 h-11 justify-center' : 'h-10 px-4 gap-2 text-[13px] font-semibold'} inline-flex items-center rounded-full transition-colors ${className}`}
        >
          {l.icon}{!compact && ` ${l.label}`}
        </a>
      ))}
    </div>
  );
}
